import { useEffect, useRef } from 'react';
import { openobserveRum } from '@openobserve/browser-rum';
import { rumLogger, useIsOpenObserveReady } from '@/features/providers/openobserve-provider';
import { useAuthData } from '@/store/auth';
import { useAccountInfo } from '@/hooks/query/account';
import { usePathname } from 'next/navigation';

export const useDeeplinkCompleteTracking = () => {
	const pathname = usePathname();
	const hasTrackedRef = useRef(false);
	const authData = useAuthData();
	const { user } = useAccountInfo();
	const isOpenObserveReady = useIsOpenObserveReady();

	useEffect(() => {
		// Prevent duplicate tracking
		if (hasTrackedRef.current) return;

		// Wait until OpenObserveProvider has finished init()+setUser(), so this
		// action carries complete usr context instead of racing with it.
		if (!isOpenObserveReady) return;

		// ✅ Critical: Ensure we're NOT on the deeplink page
		// This hook should only fire on the target page after navigation
		if (pathname.includes('/deeplink')) {
			return;
		}

		// Early exit: check for deeplink tracking data (fastest check)
		// Note: sessionStorage is only set on deeplink page, so this naturally prevents
		// tracking on non-deeplink pages
		const loginDataStr = sessionStorage.getItem('deeplink_login_start_time');
		if (!loginDataStr) return;

		// Check if login is completed
		const signinCompleted = sessionStorage.getItem('deeplink_login_signin_completed');
		if (signinCompleted !== 'true') return;

		// Wait for authData to be ready
		if (!authData?.siteName) return;

		// Mark as tracked to prevent duplicate execution
		hasTrackedRef.current = true;

		try {
			const loginData = JSON.parse(loginDataStr);

			// Function to send tracking event
			const sendCompleteEvent = () => {
				// Add a small delay to ensure URL is fully updated after Next.js client-side navigation
				requestAnimationFrame(() => {
					setTimeout(() => {
						const currentTime = performance.now();
						const totalDuration = currentTime - loginData.startTime;
						
						// Get pathname after ensuring DOM and URL are fully updated
						const finalPathname = window.location.pathname;

						try {
							// Calculate time breakdown
							const deeplinkPageDuration = loginData.routerReplaceTime 
								? loginData.routerReplaceTime - loginData.startTime 
								: undefined;
							
							const firstPageLoadTime = loginData.routerReplaceTime
								? currentTime - loginData.routerReplaceTime
								: undefined;

							openobserveRum.addAction('deeplink_login_flow_complete', {
								// Total time: Deeplink page start → First page fully loaded
								totalDuration: totalDuration,
								
								// Time breakdown (if available)
								deeplinkPageDuration: deeplinkPageDuration,  // Time spent on deeplink page
								firstPageLoadTime: firstPageLoadTime,        // Time to load first page
								
								// Page info
								targetPage: finalPathname,
								
								// User info
								username: authData?.userName,
								custid: user?.ID,
								
								// Timestamps for debugging
								timestamp: Date.now(),
								deeplinkStartTimestamp: loginData.timestamp,
								
								// Note: siteName, view, and geo.country are automatically included from OpenObserveProvider global context
							});

							rumLogger.info('Deeplink login flow complete', {
								totalDuration,
								targetPage: finalPathname,
								username: authData?.userName,
								custid: user?.ID,
							});
						} catch (error) {
							console.error('Error sending OpenObserve action:', error);
						}

						// Clear tracking data to prevent re-sending
						sessionStorage.removeItem('deeplink_login_start_time');
						sessionStorage.removeItem('deeplink_login_signin_completed');
					}, 100); // 100ms delay to ensure URL is updated
				});
			};

			// Check page load status
			if (document.readyState === 'complete') {
				// Page is already fully loaded, send immediately
				sendCompleteEvent();
			} else {
				// Wait for the page to fully load
				const handleLoad = () => {
					sendCompleteEvent();
					window.removeEventListener('load', handleLoad);
				};
				window.addEventListener('load', handleLoad);

				// Cleanup function
				return () => {
					window.removeEventListener('load', handleLoad);
				};
			}
		} catch (error) {
			console.error('Error tracking deeplink completion:', error);
			// Clear data even if an error occurs
			sessionStorage.removeItem('deeplink_login_start_time');
			sessionStorage.removeItem('deeplink_login_signin_completed');
		}
	}, [pathname, authData?.siteName, authData?.userName, user?.ID, isOpenObserveReady]);
};
